import { CourierPartner, DeliveryMode, IOrderPackage, PackageStatus } from '@admin-panel/types';
// The module path, not the barrel: the status tag owns these rules, and page tests that mock the
// components barrel must not lose them.
import {
  deliveryStatusLabel,
  isRiderMarkedUndelivered,
  isUndeliveredAttempt,
} from '@admin-panel/components/lib/order/orderpackagestatus';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const IST = 'Asia/Kolkata';

export { deliveryStatusLabel, isRiderMarkedUndelivered, isUndeliveredAttempt };

/**
 * One label for each delivery fact, shared by Follow-ups, Today, the package page and My Rides.
 * Keep the wording aligned with Fulfillment's TodayRules / timeline text.
 */
export const DELIVERY_LABELS = {
  undelivered: 'Undelivered',
  doorstepCancelled: 'Cancelled at the door',
  needsScheduling: 'Needs scheduling',
  overdue: 'Overdue',
  today: 'Today',
  upcoming: 'Upcoming',
  anyTime: 'any time',
  originalPromise: 'Original promise',
  currentEstimate: 'Current estimate',
  customerRequested: 'Customer requested',
  deliveryPromise: 'Delivery promise',
  withCourier: 'With the courier',
  custodyUnconfirmed: 'Custody not confirmed',
} as const;

const istDay = (at: Dayjs, now: Dayjs) =>
  at.format(at.year() === now.tz(IST).year() ? 'ddd, D MMM' : 'ddd, D MMM YYYY');

/**
 * A delivery day exactly as Fulfillment writes it (timeline, Today, rider app): "Mon, 26 Oct", never
 * with a year. Promises and requests are always near today, and the wording must match the server's.
 */
const deliveryDay = (at: Dayjs) => at.format('ddd, D MMM');
const calendarDay = (date: string) => dayjs.tz(`${date} 12:00`, IST);

/** "Mon, 26 Oct, 16:20 IST" (year only when not this year), the same pattern as formatToReadableDate. */
export const formatIstInstant = (value?: unknown, now: Dayjs = dayjs()) => {
  const at = parseServerInstant(value)?.tz(IST);
  return at ? `${istDay(at, now)}, ${at.format('HH:mm')} IST` : '-';
};

/** The IST calendar day alone ("Mon, 26 Oct"), for compact date columns. */
export const formatIstDate = (value?: unknown, now: Dayjs = dayjs()) => {
  if (typeof value === 'string' && isRequestedDate(value.trim()))
    return istDay(calendarDay(value.trim()), now);
  const at = parseServerInstant(value)?.tz(IST);
  return at ? istDay(at, now) : '-';
};

/** Whether a package's promise or estimate carries a real clock time (express or try at home). */
export const deliveryHasClock = (pkg?: Pick<IOrderPackage, 'isExpress' | 'tryAtHome'> | null) =>
  pkg?.isExpress === true || pkg?.tryAtHome === true;

/**
 * A delivery promise, estimate or request, always in India time. Fulfillment stores a date-only promise
 * or request as 23:59:59 IST, and only express / try-at-home promises carry a meaningful clock
 * (the rule in resolveDeliverySchedule), so anything else reads "Mon, 26 Oct (any time)".
 */
export const formatDeliveryInstant = (value: unknown, { hasClock }: { hasClock: boolean }) => {
  if (typeof value === 'string' && isRequestedDate(value.trim())) {
    return `${deliveryDay(calendarDay(value.trim()))} (${DELIVERY_LABELS.anyTime})`;
  }
  const at = parseServerInstant(value)?.tz(IST);
  if (!at) return '-';
  return hasClock && at.format('HH:mm:ss') < '23:59:59'
    ? `${deliveryDay(at)}, ${at.format('HH:mm')} IST`
    : `${deliveryDay(at)} (${DELIVERY_LABELS.anyTime})`;
};

/** The package's own promise/estimate value, with its clock rule. */
export const formatPackageDeliveryDate = (
  pkg: Pick<IOrderPackage, 'isExpress' | 'tryAtHome'> | null | undefined,
  value: unknown,
) => formatDeliveryInstant(value, { hasClock: deliveryHasClock(pkg) });

export const isOtpCancelled = (pkg?: IOrderPackage | null) =>
  Boolean(
    pkg?.courierMetadata?.RIDER_CANCELLATION_CONFIRMED_AT?.trim() ||
    pkg?.courierMetadata?.RIDER_CANCELLATION_DROPPED_AT?.trim(),
  );

export const canStartDeliveryAttempt = (pkg?: IOrderPackage | null) => !isOtpCancelled(pkg);

/* Rider follow-up badges (cancelled OTP, custody, requested timing, failure
   reason) are internal operations detail; brands on Nexus do not see them. */
export const showsRiderFollowUpBadges = (portal: string | undefined) => portal !== 'vendor';

export const failedAttempt = (pkg: IOrderPackage): Record<string, unknown> | undefined => {
  const evidence = pkg.riderActionAddress?.UNDELIVERED;
  return evidence && typeof evidence === 'object' && !Array.isArray(evidence)
    ? (evidence as Record<string, unknown>)
    : undefined;
};

/* A Shiprocket/BlueDart failed attempt (derived by Fulfillment from the courier's latest update). The
   courier holds the parcel: Hub records the customer's new date, the courier is told in its own portal. */
export const courierFailure = (pkg: IOrderPackage) =>
  [PackageStatus.DISPATCHED, PackageStatus.OUT_FOR_DELIVERY].includes(pkg.currentStatus)
    ? pkg.courierDeliveryFailure || undefined
    : undefined;

const FAILURE_REASONS: Record<string, string> = {
  CUSTOMER_NOT_RESPONDING: 'Customer not responding',
  CUSTOMER_REFUSED_DELIVERY: 'Customer refused delivery',
  CUSTOMER_REQUESTED_RESCHEDULE: 'Customer asked to reschedule',
  ADDRESS_ACCESS_ISSUE: 'Address or access issue',
  ADDRESS_ISSUE: 'Address or access issue',
  OTHER: 'Other delivery issue',
  STORE_CLOSED: 'Store closed',
  STORE_REFUSED: 'Store refused the parcel',
  DAMAGED: 'Parcel damaged',
  RIDER_ISSUE: 'Rider issue',
};
/**
 * The reasons an operator or rider records for an undelivered attempt: exactly the rider app's codes
 * (RiderUndeliveredReason), so Hub, web My Rides and the app write the same evidence.
 */
export const UNDELIVERED_REASONS = [
  'CUSTOMER_NOT_RESPONDING',
  'CUSTOMER_REFUSED_DELIVERY',
  'CUSTOMER_REQUESTED_RESCHEDULE',
  'ADDRESS_ISSUE',
].map(code => ({ value: code, label: FAILURE_REASONS[code] }));

/** Why our rider couldn't drop a store transfer at the store (2026-10-28); Other needs a note. */
export const STORE_TRANSFER_REASONS = [
  { value: 'STORE_CLOSED', label: 'Store closed' },
  { value: 'STORE_REFUSED', label: 'Store refused the parcel' },
  { value: 'DAMAGED', label: 'Parcel damaged' },
  { value: 'RIDER_ISSUE', label: 'Rider issue' },
  { value: 'OTHER', label: 'Other' },
];

export const readableFailureReason = (value?: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const text = value.trim();
  if (FAILURE_REASONS[text]) return FAILURE_REASONS[text];
  if (/^[A-Z][A-Z0-9_ -]+$/.test(text)) {
    const words = text.toLowerCase().replace(/[_-]+/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return text;
};

/* The courier's verdict is on the current AWB, so it wins over rider evidence from an earlier attempt. */
export const failureReason = (pkg: IOrderPackage) => {
  const courier = courierFailure(pkg)?.reason?.trim();
  if (courier) return readableFailureReason(courier);
  const rider = failedAttempt(pkg)?.reason;
  return readableFailureReason(rider);
};

/**
 * When the rider captured an action, from its evidence (`capturedAt`, else the epoch fields). The rider's
 * clock is the attempt time; the server's write time can be minutes or hours later (offline sync).
 */
export const capturedAt = (detail?: Record<string, unknown> | null): string | undefined => {
  if (!detail) return undefined;
  const text = parseServerInstant(detail.capturedAt);
  if (text) return text.toISOString();
  const ms = Number(detail.capturedAtEpochMs);
  if (Number.isFinite(ms) && ms > 0) return dayjs(ms).toISOString();
  const seconds = Number(detail.capturedAtEpochSeconds);
  if (Number.isFinite(seconds) && seconds > 0) return dayjs.unix(seconds).toISOString();
  return undefined;
};

export const failureAt = (pkg: IOrderPackage) =>
  courierFailure(pkg)?.failedOn ?? capturedAt(failedAttempt(pkg)) ?? undefined;


const positiveRiderId = (value: unknown) => {
  const id =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^[1-9]\d*$/.test(value.trim())
        ? Number(value.trim())
        : undefined;
  return id && Number.isSafeInteger(id) && id > 0 ? id : undefined;
};

export const previousFollowUpRiderId = (pkg: IOrderPackage) =>
  (isOtpCancelled(pkg)
    ? positiveRiderId(pkg.courierMetadata?.RIDER_CANCELLATION_RIDER_ID)
    : undefined) ?? positiveRiderId(failedAttempt(pkg)?.riderId);

export const canEditFollowUpTiming = (pkg: IOrderPackage) =>
  !isOtpCancelled(pkg) &&
  String(pkg.fulfillmentMode) !== 'DIGITAL' &&
  pkg.deliveryMode === DeliveryMode.HOME_DELIVERY &&
  [PackageStatus.PACKED, PackageStatus.DISPATCHED, PackageStatus.OUT_FOR_DELIVERY].includes(
    pkg.currentStatus,
  ) &&
  (([PackageStatus.DISPATCHED, PackageStatus.OUT_FOR_DELIVERY].includes(pkg.currentStatus) &&
    (isRiderMarkedUndelivered(pkg) || Boolean(pkg.courierDeliveryFailure))) ||
    (pkg.currentStatus === PackageStatus.PACKED &&
      Boolean(
        failedAttempt(pkg) &&
        pkg.courierMetadata?.onDemandCourierResetAt?.trim() &&
        !pkg.courierMetadata?.onDemandCourierReattemptConfirmedAt?.trim(),
      )));

/**
 * The customer's requested delivery: "Mon, 26 Oct, 16:20 IST" or "Mon, 26 Oct (any time)".
 * Undefined when no valid date was requested.
 */
export const requestedDeliveryLabel = (pkg: IOrderPackage) => {
  const date = pkg.courierMetadata?.CUSTOMER_REQUESTED_DELIVERY_DATE?.trim();
  if (!isRequestedDate(date)) return undefined;
  const time = pkg.courierMetadata?.CUSTOMER_REQUESTED_DELIVERY_TIME?.trim();
  const day = deliveryDay(calendarDay(date));
  if (time && !isRequestedTime(time)) return `${day} · time needs review`;
  return time
    ? `${day}, ${clockText(time, slotEnd(time, pkg))} IST`
    : `${day} (${DELIVERY_LABELS.anyTime})`;
};
export const hasRequestedDelivery = (pkg?: IOrderPackage) =>
  Boolean(pkg && requestedDeliveryLabel(pkg));

export const requestedTiming = (pkg: IOrderPackage) => {
  const date = pkg.courierMetadata?.CUSTOMER_REQUESTED_DELIVERY_DATE?.trim();
  return (
    requestedDeliveryLabel(pkg) ??
    (date ? 'Invalid requested date' : DELIVERY_LABELS.needsScheduling)
  );
};

/** Due state of the customer's request alone (no fallback to the estimate or promise). */
export const timingState = (pkg: IOrderPackage, now = dayjs()) => {
  const timing = resolveDeliverySchedule(pkg);
  if (timing.source !== 'REQUESTED') return undefined;
  const time = pkg.courierMetadata?.CUSTOMER_REQUESTED_DELIVERY_TIME?.trim();
  if (time && !isRequestedTime(time)) return undefined;
  return deliveryScheduleState(timing, now);
};

export const validateRequestedTiming = (date?: string, time?: string, now = dayjs()) => {
  if (!date) return 'Select a requested delivery date.';
  if (!isRequestedDate(date)) return 'Select a valid requested delivery date.';
  if (time && !isRequestedTime(time)) return 'Select a valid requested delivery time.';
  const today = now.tz(IST).format('YYYY-MM-DD');
  if (date < today) return 'Requested delivery date cannot be in the past (IST).';
  if (time && !dayjs.tz(`${date} ${time}`, IST).isAfter(now)) {
    return 'Requested delivery time must be in the future (IST).';
  }
  return undefined;
};

export const isRequestedDate = (date?: string): date is string =>
  Boolean(
    date &&
    date.slice(0, 4) !== '0000' &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    dayjs(date).isValid() &&
    dayjs(date).format('YYYY-MM-DD') === date,
  );

export const isRequestedTime = (time: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(time);

export const hasFailedDelivery = (pkg: IOrderPackage) =>
  Boolean(
    failedAttempt(pkg) ||
    pkg.riderActionAddress?.UNDELIVERED_DROPPED_AT_FACILITY ||
    isRiderMarkedUndelivered(pkg) ||
    courierFailure(pkg),
  );

export const followUpStage = (pkg: IOrderPackage) => {
  if (isOtpCancelled(pkg)) return DELIVERY_LABELS.doorstepCancelled;
  if (pkg.currentStatus === PackageStatus.RTO_INITIATED) return 'Returning / RTO';
  if (
    pkg.currentStatus === PackageStatus.OUT_FOR_DELIVERY &&
    !isRiderMarkedUndelivered(pkg) &&
    !courierFailure(pkg)
  )
    return hasFailedDelivery(pkg) ? 'Reattempt in progress' : 'Delivery in progress';
  return hasFailedDelivery(pkg) ? 'Reattempt' : 'Delivery';
};

/**
 * Our own riders and on-demand partners (Fulfillment TodayRules.OWN_FLEET). Any other courier on the
 * package is a provider (Shiprocket, BlueDart, …) that physically holds the parcel in transit and on RTO.
 * Enum values are read at call time: page tests mock @admin-panel/types per file.
 */
const isProviderCourier = (partner?: CourierPartner | null) =>
  Boolean(partner) &&
  ![
    CourierPartner.SELF,
    CourierPartner.SELF_EXPRESS,
    CourierPartner.SELF_PRIORITY,
    CourierPartner.LYSKRAFT,
    CourierPartner.PORTER,
    CourierPartner.SHADOWFAX,
  ].includes(partner as CourierPartner);

export const custodyLabel = (pkg: IOrderPackage) => {
  if (isOtpCancelled(pkg))
    return pkg.courierMetadata?.RIDER_CANCELLATION_DROPPED_AT?.trim()
      ? 'Dropped at facility'
      : 'Return to facility (custody pending)';
  if (pkg.currentStatus === PackageStatus.RTO_INITIATED)
    return pkg.riderActionAddress?.UNDELIVERED_DROPPED_AT_FACILITY
      ? 'Dropped at facility'
      : // A Shiprocket/BlueDart RTO travels back with the provider's courier.
        isProviderCourier(pkg.courierPartner)
        ? DELIVERY_LABELS.withCourier
        : DELIVERY_LABELS.custodyUnconfirmed;
  if (
    pkg.currentStatus === PackageStatus.PACKED &&
    pkg.riderActionAddress?.UNDELIVERED_DROPPED_AT_FACILITY
  )
    return 'Dropped at facility';
  if (courierFailure(pkg)) return DELIVERY_LABELS.withCourier;
  if ([PackageStatus.DISPATCHED, PackageStatus.OUT_FOR_DELIVERY].includes(pkg.currentStatus))
    return pkg.riderAssigned && pkg.riderId && pkg.riderId > 0
      ? 'With assigned rider'
      : 'Custody not confirmed';
  return pkg.currentStatus === PackageStatus.PACKED ? 'At facility' : 'Custody not confirmed';
};

/**
 * A server timestamp as an instant. Rider evidence stores Java's ZonedDateTime text, which ends in a
 * zone id ("…+05:30[Asia/Kolkata]") that dayjs cannot parse; the offset before it is what counts.
 */
export const parseServerInstant = (value?: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const parsed = dayjs(value.trim().replace(/\[[^\]]*\]$/, ''));
  return parsed.isValid() ? parsed : undefined;
};

/** A recorded moment (an attempt, a failure): always with its clock. */
export const formatFollowUpTimestamp = (value?: unknown, now: Dayjs = dayjs()) =>
  formatIstInstant(value, now);

export type DeliverySchedulePackage = IOrderPackage & { tryAtHomeSlot?: string };
export type DeliveryScheduleTiming = {
  date?: string;
  time?: string;
  /** End of the customer's slot (a rider reschedule, "14:00-16:00"): due until then, not overdue from its start. */
  endTime?: string;
  source: 'REQUESTED' | 'ESTIMATED' | 'PROMISED' | 'UNTIMED';
};

const promiseTimestamp = (value?: string) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}T/.test(value) || !isRequestedDate(value.slice(0, 10)))
    return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(IST) : undefined;
};

// Keep these read-only rules aligned with Fulfillment's DeliveryFollowUpTiming / SQL.
export const resolveDeliverySchedule = (pkg: DeliverySchedulePackage): DeliveryScheduleTiming => {
  const date = pkg.courierMetadata?.CUSTOMER_REQUESTED_DELIVERY_DATE?.trim();
  if (isRequestedDate(date)) {
    const time = pkg.courierMetadata?.CUSTOMER_REQUESTED_DELIVERY_TIME?.trim();
    const valid = time && isRequestedTime(time) ? time : undefined;
    return { date, time: valid, endTime: slotEnd(valid, pkg), source: 'REQUESTED' };
  }
  const estimate = promiseTimestamp(pkg.estimatedDeliveryDate);
  const selected =
    pkg.estimatedDeliveryDate != null ? estimate : promiseTimestamp(pkg.promisedDeliveryDate);
  if (!selected) return { source: 'UNTIMED' };
  const hasClock = pkg.isExpress === true || pkg.tryAtHome === true;
  return {
    date: selected.format('YYYY-MM-DD'),
    time:
      hasClock && selected.format('HH:mm:ss') < '23:59:59' ? selected.format('HH:mm') : undefined,
    source: pkg.estimatedDeliveryDate != null ? 'ESTIMATED' : 'PROMISED',
  };
};

/** The stored slot end when it follows a valid start (Fulfillment's requestedWindowEnd). */
const slotEnd = (start: string | undefined, pkg: IOrderPackage) => {
  const end = pkg.courierMetadata?.CUSTOMER_REQUESTED_DELIVERY_TIME_END?.trim();
  return start && end && isRequestedTime(end) && end > start ? end : undefined;
};

/** "14:00" or a slot "14:00–16:00". */
export const clockText = (time?: string, endTime?: string) =>
  time ? (endTime ? `${time}\u2013${endTime}` : time) : undefined;

/** A resolved schedule (requested, estimate or promise) in the shared wording. */
export const formatScheduleTiming = (
  timing: Pick<DeliveryScheduleTiming, 'date' | 'time' | 'endTime'>,
) => {
  if (!timing.date) return undefined;
  const day = deliveryDay(calendarDay(timing.date));
  return timing.time
    ? `${day}, ${clockText(timing.time, timing.endTime)} IST`
    : `${day} (${DELIVERY_LABELS.anyTime})`;
};

export const formatPromiseDate = (pkg: DeliverySchedulePackage) =>
  promiseTimestamp(pkg.promisedDeliveryDate)
    ? formatPackageDeliveryDate(pkg, pkg.promisedDeliveryDate)
    : '-';

export type DeliveryScheduleState =
  | typeof DELIVERY_LABELS.overdue
  | typeof DELIVERY_LABELS.today
  | typeof DELIVERY_LABELS.upcoming;

/**
 * The one overdue rule (IST): a timed due is overdue once its time passes (a slot once it ends), a date-only one
 * the next day.
 */
export const deliveryScheduleState = (
  timing: Pick<DeliveryScheduleTiming, 'date' | 'time' | 'endTime'>,
  now = dayjs(),
): DeliveryScheduleState | undefined => {
  if (!timing.date) return undefined;
  const current = now.tz(IST);
  const dueBy = timing.time ? timing.endTime ?? timing.time : undefined;
  if (dueBy && dayjs.tz(`${timing.date} ${dueBy}`, IST).isBefore(current))
    return DELIVERY_LABELS.overdue;
  if (timing.date < current.format('YYYY-MM-DD')) return DELIVERY_LABELS.overdue;
  return timing.date === current.format('YYYY-MM-DD')
    ? DELIVERY_LABELS.today
    : DELIVERY_LABELS.upcoming;
};

/** Whole IST days a delivery date is after another (e.g. delivered vs promised); never browser days. */
export const istDaysBetween = (from?: unknown, to?: unknown) => {
  const a = parseServerInstant(from)?.tz(IST).format('YYYY-MM-DD');
  const b = parseServerInstant(to)?.tz(IST).format('YYYY-MM-DD');
  return a && b ? dayjs(b).diff(dayjs(a), 'day') : undefined;
};

export const resolveFollowUpScope = (
  portal: string,
  warehouseId?: number | string,
  _warehouseIds?: number[],
  all = false,
) => {
  if (portal === 'admin' || all) return { ready: true, facilityIds: undefined };
  const ids = (typeof warehouseId === 'number' ? [warehouseId] : []).filter(
    id => Number.isInteger(id) && id > 0,
  );
  return { ready: ids.length > 0, facilityIds: ids };
};
